import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Phone, ShieldCheck } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

/**
 * Two-step OTP flow to update the customer's mobile number. Owns local state for
 * the new mobile, OTP, and step. Calls onUpdated on success so the parent can
 * refresh the displayed mobile.
 */
const ChangeMobileForm = ({ user, authClient, onUpdated, onDone }) => {
  const { toast } = useToast();
  const [newMobile, setNewMobile] = useState('');
  const [step, setStep] = useState(1); // 1 = enter mobile, 2 = enter OTP
  const [otp, setOtp] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [devOtp, setDevOtp] = useState('');

  const reset = () => { setNewMobile(''); setStep(1); setOtp(''); setDevOtp(''); onDone && onDone(); };

  const onSendOtp = async () => {
    const clean = newMobile.replace(/\D/g, '').slice(-10);
    if (clean.length !== 10) return toast({ title: 'Enter a valid 10-digit mobile', variant: 'destructive' });
    if (clean === user?.mobile) return toast({ title: 'New mobile is the same as your current mobile', variant: 'destructive' });
    setSending(true);
    try {
      const r = await authClient.post('/auth/me/change-mobile/send-otp', { new_mobile: clean });
      if (r.data?.dev_otp) setDevOtp(r.data.dev_otp);
      setNewMobile(clean); setStep(2);
      toast({ title: `OTP sent to +91 ${clean}` });
    } catch (err) {
      toast({ title: 'Send OTP failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setSending(false); }
  };

  const onVerifyOtp = async () => {
    if (!/^\d{4,6}$/.test(otp.trim())) return toast({ title: 'Enter the OTP code', variant: 'destructive' });
    setVerifying(true);
    try {
      const r = await authClient.post('/auth/me/change-mobile/verify', { new_mobile: newMobile, otp: otp.trim() });
      localStorage.setItem('tgmart_user', JSON.stringify(r.data));
      window.dispatchEvent(new Event('tgmart-session-change'));
      onUpdated && onUpdated(r.data);
      toast({ title: 'Mobile number updated successfully' });
      reset();
    } catch (err) {
      toast({ title: 'Verify failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setVerifying(false); }
  };

  return (
    <div className="mt-3 space-y-3 border-t border-gray-200 pt-3" data-testid="change-mobile-form">
      {step === 1 ? (
        <>
          <Label className="text-xs text-gray-700">New mobile number</Label>
          <div className="flex gap-2 flex-wrap">
            <Input value={newMobile} onChange={(e) => setNewMobile(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit mobile" inputMode="numeric" className="max-w-xs" data-testid="new-mobile-input" />
            <Button type="button" onClick={onSendOtp} disabled={sending || newMobile.length !== 10} className="bg-[#2c54b2] hover:bg-[#1f3f87]" data-testid="send-cm-otp-btn">
              {sending ? 'Sending…' : 'Send OTP'}
            </Button>
            <Button type="button" variant="ghost" onClick={reset} data-testid="cancel-cm-btn">Cancel</Button>
          </div>
        </>
      ) : (
        <>
          <Label className="text-xs text-gray-700">
            Enter OTP sent to <span className="font-mono">+91 {newMobile}</span>
          </Label>
          {devOtp && (
            <div className="text-xs bg-yellow-50 border border-yellow-200 rounded px-2 py-1 inline-block">
              Dev OTP: <span className="font-mono font-bold">{devOtp}</span>
            </div>
          )}
          <div className="flex gap-2 flex-wrap">
            <Input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="OTP" inputMode="numeric" className="max-w-[140px] font-mono text-center text-lg" data-testid="cm-otp-input" />
            <Button type="button" onClick={onVerifyOtp} disabled={verifying} className="bg-[#ee5e23] hover:bg-[#d24e15]" data-testid="verify-cm-otp-btn">
              <ShieldCheck className="w-4 h-4 mr-1" /> {verifying ? 'Verifying…' : 'Verify & update'}
            </Button>
            <Button type="button" variant="ghost" onClick={() => { setStep(1); setOtp(''); setDevOtp(''); }} data-testid="cm-resend-btn">
              Change number
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export { ChangeMobileForm };
export default ChangeMobileForm;
